import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useGameStore } from '../../store/game-store.js';
import GuessInput from './GuessInput.jsx';
import StatsBar from './StatsBar.jsx';
import TargetStatus from './TargetStatus.jsx';
import GuessHistory from './GuessHistory.jsx';
import WinPanel from '../WinPanel/WinPanel.jsx';
import Confetti from '../shared/Confetti.jsx';
import './GameView.css';

export default function GameView() {
  const {
    gameController, targets, stats, updateStats,
    won, winResult, firstWin, setWin,
    addGuess, addToast, setView, endGame,
    gameOptions,
  } = useGameStore();

  const boardRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // Initialize the game controller with the board element
  useEffect(() => {
    if (!gameController || !boardRef.current || initialized) return;

    const controller = gameController;

    // Wire up event listeners
    controller.on('statsUpdate', (newStats) => {
      updateStats(newStats);
    });

    controller.on('win', ({ result, firstWin: fw, isNewBest }) => {
      setWin(result, fw, isNewBest);
      if (fw) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    });

    controller.on('nodeAdded', ({ label, type }) => {
      if (gameOptions.toastNotifications) {
        const prefix = type === 'actor' ? 'New Star' : 'New Title';
        addToast(`${prefix}: ${label}`, 'success');
      }
    });

    controller.on('guess', (result) => {
      addGuess(result);
    });

    // Initialize
    controller.init(boardRef.current).then(() => {
      setInitialized(true);
    });

    // Set up node selection
    const setupSelection = () => {
      if (!controller.board?.cy) return;
      controller.board.cy.on('tap', 'node', (e) => {
        const data = e.target.data();
        setSelectedNode(data);
      });
      controller.board.cy.on('tap', (e) => {
        if (e.target === controller.board.cy) {
          setSelectedNode(null);
        }
      });
    };

    controller.on('initialized', setupSelection);

    return () => {
      // Cleanup handled by endGame
    };
  }, [gameController, boardRef.current]);

  // Desktop: refocus input on canvas click
  const handleCanvasClick = useCallback((e) => {
    if (e.target === boardRef.current || e.target.tagName === 'CANVAS') {
      const input = document.querySelector('.guess-input__field');
      if (input && window.innerWidth >= 768) {
        input.focus();
      }
    }
  }, []);

  const handleBack = () => {
    if (gameController) {
      gameController.destroy();
    }
    endGame();
    setView('home');
  };

  const handleHint = async (type) => {
    if (!gameController || !selectedNode) return;

    const nodeId = selectedNode.id;

    if (type === 'media' && nodeId.startsWith('a')) {
      const name = await gameController.hintMediaFromActor(nodeId);
      if (name) addToast(`Hint: ${name}`, 'info');
      else addToast('No hints available for this actor', 'warning');
    } else if (type === 'actor' && (nodeId.startsWith('m') || nodeId.startsWith('t'))) {
      const name = await gameController.hintActorFromMedia(nodeId);
      if (name) addToast(`Hint: ${name}`, 'info');
      else addToast('No hints available for this title', 'warning');
    } else if (type === 'bridge') {
      const name = await gameController.hintBridge();
      if (name) addToast(`Bridge: ${name}`, 'info');
      else addToast('No bridge found', 'warning');
    }
  };

  const isExploration = gameOptions.isExploration;

  return (
    <div className="game" onClick={handleCanvasClick}>
      <Confetti active={showConfetti} />

      <header className="game__header">
        <button className="game__back" onClick={handleBack}>
          &#x2190;
        </button>
        <StatsBar stats={stats} timeLimit={gameOptions.timeLimit} />
        {isExploration && <span className="game__mode-label">Explore</span>}
      </header>

      <TargetStatus
        targets={targets}
        controller={gameController}
        won={won}
      />

      <div className="game__board" ref={boardRef} />

      {/* Hint buttons when a node is selected */}
      {selectedNode && !won && (
        <div className="game__hints">
          {selectedNode.id.startsWith('a') && (
            <button className="game__hint-btn" onClick={() => handleHint('media')}>
              Reveal Film
            </button>
          )}
          {(selectedNode.id.startsWith('m') || selectedNode.id.startsWith('t')) && (
            <button className="game__hint-btn" onClick={() => handleHint('actor')}>
              Reveal Actor
            </button>
          )}
          <button className="game__hint-btn game__hint-btn--bridge" onClick={() => handleHint('bridge')}>
            Bridge
          </button>
        </div>
      )}

      <GuessInput controller={gameController} disabled={!initialized} />

      <GuessHistory />

      {won && <WinPanel />}
    </div>
  );
}
